import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { createConversation, formatApiError, getAgent, sendMessage } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Bot, ExternalLink, FlaskConical, Loader2, MessageSquare, RefreshCw, RotateCcw, Send, Settings2, Sparkles } from 'lucide-react';

const categoryLabels = {
  customer_service: 'Customer Service',
  appointment_setting: 'Appointment Setting',
  sales: 'Sales',
  routing: 'Routing Agent',
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const chunkTypingDelay = (text) => {
  const len = (text || '').trim().length;
  return Math.max(400, Math.min(1100, 260 + len * 8));
};

const AgentDashboardPage = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const messagesContainerRef = useRef(null);

  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);

  useEffect(() => {
    const loadAgent = async () => {
      try {
        const { data } = await getAgent(agentId);
        setAgent(data);
      } catch (err) {
        console.error(err);
        setError(formatApiError(err));
      } finally {
        setLoading(false);
      }
    };

    loadAgent();
  }, [agentId]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, [chatMessages, chatLoading]);

  const quickPrompts = useMemo(() => {
    const prompts = [
      'Give me your opening introduction.',
      'How would you handle a new inbound lead?',
      'What kind of customer requests are you best at?',
    ];
    return prompts;
  }, []);

  const handleStartNewSession = () => {
    setConversationId(null);
    setChatMessages([]);
    setChatInput('');
    setError('');
  };

  const handleRestartAgentView = async () => {
    if (chatLoading) return;
    setError('');
    setLoading(true);
    try {
      const { data } = await getAgent(agentId);
      setAgent(data);
      setConversationId(null);
      setChatMessages([]);
      setChatInput('');
      window.location.reload();
    } catch (err) {
      console.error(err);
      setError(formatApiError(err));
      setLoading(false);
    }
  };

  const handleSendMessage = async (preset) => {
    const content = (preset || chatInput).trim();
    if (!content || !agentId || chatLoading) return;

    if (!preset) setChatInput('');
    setChatMessages((prev) => [...prev, { role: 'user', content }]);
    setChatLoading(true);

    try {
      let convId = conversationId;
      if (!convId) {
        const conversation = await createConversation({ agent_id: agentId, channel: 'web' });
        convId = conversation.data.id;
        setConversationId(convId);
      }

      const response = await sendMessage(convId, content);
      const assistant = response.data?.assistant_message;
      const assistantChunks = Array.isArray(assistant?.chunks) && assistant.chunks.length > 0
        ? assistant.chunks
        : (assistant?.content ? [assistant.content] : []);

      if (assistantChunks.length > 0) {
        for (let i = 0; i < assistantChunks.length; i += 1) {
          const chunk = assistantChunks[i];
          if (i > 0) {
            await wait(chunkTypingDelay(chunk));
          }
          setChatMessages((prev) => [...prev, { role: 'assistant', content: chunk }]);
        }
      }
    } catch (err) {
      console.error(err);
      setChatMessages((prev) => [...prev, { role: 'assistant', content: 'Sorry — something went wrong while messaging this agent.' }]);
    } finally {
      setChatLoading(false);
    }
  };

  if (loading) {
    return <Layout><div className="p-8 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;
  }

  return (
    <Layout>
      <div className="p-8 space-y-6" data-testid="agent-dashboard-page">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <Link to="/agents" className="inline-flex items-center gap-2 hover:text-foreground">
            <ArrowLeft className="w-4 h-4" /> Back to Agent Dashboard
          </Link>
        </div>

        {error ? <div className="apollo-card p-4 text-sm text-red-400">{error}</div> : null}

        {agent ? (
          <>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <Badge variant="outline">{categoryLabels[agent.category] || agent.category || 'Agent'}</Badge>
                  <Badge variant="outline">{agent.is_active ? 'Active' : 'Inactive'}</Badge>
                </div>
                <h1 className="text-3xl font-semibold tracking-tight font-['Outfit']">{agent.name}</h1>
                <p className="text-muted-foreground mt-1">{agent.description || 'This agent is ready for interaction and review.'}</p>
              </div>
              <div className="flex items-center gap-3 flex-wrap justify-end">
                <Button variant="outline" asChild>
                  <Link to={`/agents/${agent.id}/edit`}><Settings2 className="w-4 h-4 mr-2" /> Configure</Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to={`/agents/${agent.id}/widget`}><ExternalLink className="w-4 h-4 mr-2" /> Widget</Link>
                </Button>
                <Button asChild>
                  <Link to={`/agents/${agent.id}/proving-grounds`}><FlaskConical className="w-4 h-4 mr-2" /> Advanced testing</Link>
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-[1.35fr_0.9fr] gap-6">
              <Card className="apollo-card overflow-hidden">
                <CardHeader className="border-b border-border">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <CardTitle className="flex items-center gap-2"><MessageSquare className="w-5 h-5 text-primary" /> Agent chat</CardTitle>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={handleStartNewSession} disabled={chatLoading}>
                        <RotateCcw className="w-4 h-4 mr-2" /> New session
                      </Button>
                      <Button variant="outline" size="sm" onClick={handleRestartAgentView} disabled={chatLoading || loading}>
                        <RefreshCw className="w-4 h-4 mr-2" /> Restart agent
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div ref={messagesContainerRef} className="min-h-[520px] max-h-[520px] overflow-y-auto p-4 space-y-3 bg-secondary/10">
                    {agent.greeting_message && chatMessages.length === 0 ? (
                      <div className="chat-message assistant">{agent.greeting_message}</div>
                    ) : null}
                    {chatMessages.length === 0 && !agent.greeting_message ? (
                      <div className="h-full flex items-center justify-center text-center px-6 py-20">
                        <div>
                          <Bot className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                          <p className="text-sm font-medium">Start a conversation</p>
                          <p className="text-xs text-muted-foreground mt-1">Use this agent view to interact directly with the agent.</p>
                        </div>
                      </div>
                    ) : null}
                    {chatMessages.map((message, index) => (
                      <div key={`${message.role}-${index}`} className={`chat-message ${message.role}`}>{message.content}</div>
                    ))}
                    {chatLoading ? <div className="chat-message assistant"><Loader2 className="w-4 h-4 animate-spin" /></div> : null}
                  </div>
                  <div className="p-4 border-t border-border bg-card space-y-3">
                    <div className="flex flex-wrap gap-2">
                      {quickPrompts.map((prompt) => (
                        <button key={prompt} type="button" onClick={() => handleSendMessage(prompt)} className="rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground hover:bg-secondary">
                          {prompt}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()} placeholder="Message this agent..." disabled={chatLoading} />
                      <Button onClick={() => handleSendMessage()} disabled={chatLoading || !chatInput.trim()}>
                        {chatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-6">
                <Card className="apollo-card">
                  <CardHeader>
                    <CardTitle>Overview</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 text-sm text-muted-foreground">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">Display name</div>
                      <div className="font-medium mt-1 text-foreground">{agent.display_name_effective || agent.display_name || agent.name}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">Primary channel behavior</div>
                      <div className="mt-1">{agent.greeting_message || 'No greeting configured yet.'}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">Model</div>
                      <div className="mt-1">{agent.llm_provider} / {agent.llm_model}</div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="apollo-card">
                  <CardHeader>
                    <CardTitle>What to do here</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm text-muted-foreground">
                    <div className="flex gap-2"><Sparkles className="w-4 h-4 mt-0.5 text-primary" /> Chat with the agent directly.</div>
                    <div className="flex gap-2"><Sparkles className="w-4 h-4 mt-0.5 text-primary" /> Review how it sounds and responds.</div>
                    <div className="flex gap-2"><Sparkles className="w-4 h-4 mt-0.5 text-primary" /> Use Configure to change setup, not this page.</div>
                    <div className="flex gap-2"><Sparkles className="w-4 h-4 mt-0.5 text-primary" /> Use Advanced testing for deeper QA and feedback routing.</div>
                  </CardContent>
                </Card>

                {conversationId ? (
                  <Card className="apollo-card">
                    <CardHeader>
                      <CardTitle>Session</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground">
                      <div className="font-mono break-all">{conversationId}</div>
                    </CardContent>
                  </Card>
                ) : null}
              </div>
            </div>
          </>
        ) : null}
      </div>
    </Layout>
  );
};

export default AgentDashboardPage;
